import { Button, Text } from '@react-spectrum/s2';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { useEffect, useRef } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { useExpressApiContext } from '@/api/ExpressApiContext';
import { useUserSettings } from '@/hooks/useUserSettings';

import type { ExpressConnectionCard as ExpressConnectionCardMessage } from '../../../../../bridge/types/messages.ts';

import { MessageRow } from './MessageRow';

const cardStyle = style({
	backgroundColor: 'layer-1',
	borderColor: 'gray-300',
	borderRadius: 'lg',
	borderStyle: 'solid',
	borderWidth: 1,
	display: 'flex',
	flexDirection: 'column',
	gap: 12,
	maxWidth: 640,
	padding: 16,
	width: 'full',
});

const headerStyle = style({
	display: 'flex',
	flexDirection: 'column',
	gap: 4,
});

const actionsStyle = style({
	display: 'flex',
	gap: 8,
});

const descriptionStyle = style({
	color: 'gray-700',
	font: 'body',
	fontSize: 'body-sm',
});

export const ExpressConnectionCard = ({
	message,
	chatId,
}: {
	message: ExpressConnectionCardMessage;
	chatId: string;
}) => {
	const bridgeApi = useBridgeApi();
	const { beginExpressConnection, hasValidInstagramConnection } =
		useExpressApiContext();
	const { settings } = useUserSettings();
	const reportedRef = useRef(false);

	// Reconnect cards are sent while the connection is still valid but expiring
	// soon, so "still connected" doesn't mean done — the card completes only
	// once the connection is renewed after the card was sent (mirrors
	// InstagramConnectionCard's isReconnect/isConnectionRenewed handling).
	const isReconnect = message.variant === 'reconnect';
	const connectionUpdateAt =
		settings?.express_connection?.calendar?.[0]?.channels.find(
			(ch) => ch.type === 'instagram',
		)?.connectionUpdateAt;
	const isConnectionRenewed =
		!!connectionUpdateAt &&
		new Date(connectionUpdateAt) > new Date(message.timestamp);
	const isCompleted =
		message.status === 'completed' ||
		(isReconnect ? isConnectionRenewed : hasValidInstagramConnection);

	// Only the card the user acted on observes the pending→complete transition
	// while mounted. Cards that mount already complete (renewed elsewhere —
	// another thread, the settings page) render as done but stay silent:
	// reporting completion forces an agent turn in this chat, which must only
	// happen where the user actually (re)connected.
	const completedAtMountRef = useRef(isCompleted);

	useEffect(() => {
		if (message.status !== 'pending') return;
		if (completedAtMountRef.current) return;
		if (!isCompleted || reportedRef.current) return;
		reportedRef.current = true;
		bridgeApi.sendAction({
			type: 'report_express_connection_card_completed',
			chat_id: chatId,
			request_id: message.request_id,
		});
	}, [bridgeApi, chatId, isCompleted, message.request_id, message.status]);

	const handleConnect = () => {
		if (message.status !== 'pending') return;
		beginExpressConnection();
	};

	return (
		<MessageRow justify="start">
			<div className={cardStyle}>
				<div className={headerStyle}>
					<Text
						styles={style({
							font: 'body',
							fontWeight: 'bold',
							fontSize: 'body',
						})}
					>
						{message.title}
					</Text>
					<Text styles={descriptionStyle}>{message.description}</Text>
				</div>
				<div className={actionsStyle}>
					<Button
						variant="primary"
						onPress={handleConnect}
						isDisabled={isCompleted}
					>
						{isReconnect
							? isCompleted
								? 'Reconnected'
								: 'Reconnect'
							: isCompleted
								? 'Connected'
								: 'Connect Express'}
					</Button>
				</div>
			</div>
		</MessageRow>
	);
};
